import { useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { accionTeclaVisor, vistaVisor, type MediaTipo } from "@/components/ui/media-tipo";
import { ZoomImagen, ZoomPdf } from "@/components/ui/zoom-archivo";
import { useTipoArchivoVisor } from "@/hooks/useTipoArchivoVisor";

/**
 * Visor (lightbox) de fotos y videos en la misma pantalla, con ‹ ›, Esc y clic afuera para
 * cerrar. Un PDF que llegue aquí (p. ej. marcado como "foto") va al visor nativo, sin la barra
 * de zoom propia, que es solo para imágenes (estándar: `ZoomPdf`).
 *
 * Nació en la evidencia del Portal de Tickets (`TicketEvidencia`) y lo reusan cobranza
 * (Atención a Clientes, pagos devueltos) y autorizaciones: es UI sin dominio, por eso vive
 * aquí. `TicketEvidencia` lo sigue re-exportando para no romper a quien lo importa por ahí.
 *
 * Es un Dialog de Radix anidado, no un portal suelto: casi siempre se abre desde una hoja o un
 * modal (detalle del ticket) y con un `keydown` en `window` el Esc cerraba el visor Y la hoja de
 * atrás, porque Radix escucha Esc en `document` en fase de captura. Como capa de Radix, Esc
 * cierra solo la de arriba, el foco queda dentro del visor y la hoja no ve el clic.
 */
export type VisorItem = { src: string; tipo: MediaTipo; nombre?: string };

export function MediaLightbox({
  items,
  index,
  onClose,
  onIndex,
}: {
  items: VisorItem[];
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  const total = items.length;
  const contenidoRef = useRef<HTMLDivElement>(null);
  const cerrarRef = useRef<HTMLButtonElement>(null);

  const it = items[index];
  const tipoArchivo = useTipoArchivoVisor(it && it.tipo !== "video" ? it.src : null, it?.nombre);
  if (!it) return null;
  const vista = vistaVisor(it.tipo, tipoArchivo);
  const varios = total > 1;

  return (
    <DialogPrimitive.Root open onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[120] bg-black/80" />
        <DialogPrimitive.Content
          ref={contenidoRef}
          aria-describedby={undefined}
          className="pointer-events-auto fixed inset-0 z-[120] flex items-center justify-center p-4 outline-none"
          onClick={onClose}
          onKeyDown={(e) => {
            const siguiente = accionTeclaVisor(e.key, index, total);
            if (siguiente === null) return;
            e.preventDefault();
            onIndex(siguiente);
          }}
          // El foco va a la foto para que + - 0 funcionen sin un clic previo; si es video, a cerrar.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const zoom = contenidoRef.current?.querySelector<HTMLElement>("[data-visor-zoom]");
            (zoom ?? cerrarRef.current)?.focus({ preventScroll: true });
          }}
        >
          <DialogPrimitive.Title className="sr-only">Visor de evidencia</DialogPrimitive.Title>
          <button
            ref={cerrarRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Cerrar visor"
            className="absolute right-3 top-3 grid size-9 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X className="size-5" />
          </button>

          {varios && (
            <>
              <button
                type="button"
                aria-label="Anterior"
                onClick={(e) => {
                  e.stopPropagation();
                  onIndex((index - 1 + total) % total);
                }}
                className="absolute left-3 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              >
                <ChevronLeft className="size-6" />
              </button>
              <button
                type="button"
                aria-label="Siguiente"
                onClick={(e) => {
                  e.stopPropagation();
                  onIndex((index + 1) % total);
                }}
                className="absolute right-3 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              >
                <ChevronRight className="size-6" />
              </button>
            </>
          )}

          <figure className="flex max-h-full max-w-full flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {vista === "imagen" ? (
              // Zoom solo sobre la foto (rueda/pellizco); fuera del panel el clic sigue cerrando.
              <ZoomImagen src={it.src} alt={it.nombre || "foto"} className="h-[82vh] w-[92vw] max-w-6xl rounded-lg" />
            ) : vista === "pdf" ? (
              <ZoomPdf
                key={it.src}
                src={it.src}
                title={it.nombre || "Documento"}
                className="h-[82vh] w-[92vw] max-w-6xl overflow-hidden rounded-lg bg-background"
              />
            ) : vista === "cargando" ? (
              <div className="grid h-[82vh] w-[92vw] max-w-6xl place-items-center text-white/80">
                <Loader2 className="size-6 animate-spin" aria-label="Cargando archivo" />
              </div>
            ) : (
              <video src={it.src} controls autoPlay className="max-h-[82vh] max-w-full rounded-lg" />
            )}
            {(it.nombre || varios) && (
              <figcaption className="text-xs text-white/80">
                {it.nombre}
                {varios ? ` - ${index + 1}/${total}` : ""}
              </figcaption>
            )}
          </figure>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
